import { FunctionComponent } from 'react'
import { makeStyles } from '@material-ui/styles'
import { ArrowUpRight } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import colors from '../../colors'

/**
 * The call to action on a card's empty state.
 *
 * A brewery with no members is the one that most needs telling what to do
 * next, and "no data" tells it nothing. Cards take an optional action and
 * render it here; the dashboard decides what the action should be, since it
 * is the thing that knows whether a signup link exists.
 *
 * Renders nothing when there is no action, so cards whose empty state needs
 * no follow-up (renewals, which simply have not come round yet) stay quiet.
 */

export type EmptyAction = { label: string, to?: string, href?: string }

const EmptyStateAction: FunctionComponent<{ action?: EmptyAction }> = ({ action }) => {
  const styles = useStyles()
  const navigate = useNavigate()

  if (!action) return null

  if (action.href) {
    return (
      <a className={styles.action} href={action.href} target="_blank" rel="noreferrer">
        {action.label}
        <ArrowUpRight size={14} style={{ marginLeft: 4 }} />
      </a>
    )
  }

  if (!action.to) return null

  return (
    <button type="button" className={styles.action} onClick={() => navigate(action.to as string)}>
      {action.label}
      <ArrowUpRight size={14} style={{ marginLeft: 4 }} />
    </button>
  )
}

const useStyles = makeStyles({
  action: {
    display: 'inline-flex',
    alignItems: 'center',
    marginTop: 6,
    padding: 0,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: colors.BRIGHT_BLUE,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
})

export default EmptyStateAction
